import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { SpecraMetricItem } from '../lieutenants/metric-item';
const LOADING_METRICS = [
    1,
    2,
    3
].map((i)=>({
        id: `loading-${i}`,
        label: '\u00a0',
        value: null
    }));
// Literal classes so Tailwind emits them.
const SM_COLUMNS = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5'
};
const LG_COLUMNS = {
    6: 'lg:grid-cols-6',
    7: 'lg:grid-cols-7',
    8: 'lg:grid-cols-8',
    9: 'lg:grid-cols-9',
    10: 'lg:grid-cols-10',
    11: 'lg:grid-cols-11',
    12: 'lg:grid-cols-12'
};
// A tile's rules and pads at one breakpoint: a rule on its left unless it
// starts a row, a rule above unless it is in the first row, and no outer pad
// at either end of a row.
const EDGE = {
    sm: {
        rowStart: 'sm:border-l-0 sm:pl-0',
        afterStart: 'sm:border-l sm:pl-[var(--space-5)]',
        rowEnd: 'sm:pr-0',
        beforeEnd: 'sm:pr-[var(--space-5)]',
        firstRow: 'sm:border-t-0',
        laterRow: 'sm:border-t'
    },
    lg: {
        rowStart: 'lg:border-l-0 lg:pl-0',
        afterStart: 'lg:border-l lg:pl-[var(--space-5)]',
        rowEnd: 'lg:pr-0',
        beforeEnd: 'lg:pr-[var(--space-5)]',
        firstRow: 'lg:border-t-0',
        laterRow: 'lg:border-t'
    }
};
/**
 * Columns per breakpoint for n metrics, so every row is full. Up to five sit
 * on one row from sm. More sit on one row from lg and, from sm, on the
 * largest row of at most five that divides them evenly (six is 3 × 2); a
 * count with no such row stays stacked until lg.
 */ function stripLayout(n) {
    if (n <= 5) return {
        sm: Math.max(n, 1)
    };
    const lg = Math.min(n, 12);
    for(let c = 5; c >= 2; c--)if (lg % c === 0) return {
        sm: c,
        lg
    };
    return {
        lg
    };
}
function edgeClasses(i, n, bp, cols) {
    if (!cols) return '';
    const e = EDGE[bp];
    const col = i % cols;
    return cn(col === 0 ? e.rowStart : e.afterStart, col === cols - 1 || i === n - 1 ? e.rowEnd : e.beforeEnd, i < cols ? e.firstRow : e.laterRow);
}
/**
 * Responsive KPI collection used by program, risk, audit, and touchpoint screens.
 *
 * A KPI strip never leaves a ragged row: its columns always divide its
 * metrics (see stripLayout), a rule sits only between tiles, and narrow
 * phones stack the tiles in one column with a rule between each.
 */ export function SpecraMetricStrip({ className, getItemProps, density, getItemTestId, loading = false, metrics: metricsProp, renderLink, ...props }) {
    const metrics = loading && metricsProp.length === 0 ? LOADING_METRICS : metricsProp;
    const n = metrics.length;
    const layout = stripLayout(n);
    return /*#__PURE__*/ _jsx("div", {
        className: cn('grid border-y border-border', layout.sm && SM_COLUMNS[layout.sm], layout.lg && LG_COLUMNS[layout.lg], className),
        "aria-busy": loading || undefined,
        ...props,
        children: metrics.map((metric, i)=>{
            const itemProps = getItemProps?.(metric);
            return /*#__PURE__*/ _jsx(SpecraMetricItem, {
                ...itemProps,
                className: cn('border-border', i > 0 && 'border-t', i === 0 && 'pl-0', i === n - 1 && 'pr-0', edgeClasses(i, n, 'sm', layout.sm), edgeClasses(i, n, 'lg', layout.lg), itemProps?.className),
                "data-item-id": metric.id,
                "data-testid": getItemTestId?.(metric),
                density: density,
                loading: loading,
                metric: metric,
                renderLink: renderLink
            }, metric.id);
        })
    });
}
